import { Component, OnDestroy, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, of } from 'rxjs';
import { catchError, distinctUntilChanged, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { CompanyService } from '../../services/company.service';
import { AuthenticationService } from '../../services/authentication.service';
import { publicMethods } from '../../globals';
import { companyProfileSafeUrl } from '../company-profile-template/company-profile-contact-links.component';
import { companyProfileOfferingItems } from '../company-profile-template/company-profile-offerings.component';
import { CompanyProfileInsight, formatInsightDate, insightConfidenceColour, insightConfidenceLabel } from '../company-profile-template/profile-insight';
import { TaxInsightGroup } from '../tax-accountancy-company-profile/tax-profile-insights';
import { bankingSegment, FinancialServicesInsights, FinancialServicesView, financialServicesInsights } from './financial-services-profile-insights';

/** loading → ready | error | unavailable (the plan has no Financial Services mode). */
export type FinancialServicesInsightsState = 'loading' | 'ready' | 'error' | 'unavailable';

@Component({
  selector: 'financial-services-company-profile',
  templateUrl: './financial-services-company-profile.component.html',
  styleUrls: ['./financial-services-company-profile.component.css'],
  standalone: false,
})
export class FinancialServicesCompanyProfileComponent implements OnInit, OnDestroy {
  private readonly destroyed$ = new Subject<void>();
  private readonly insightRequests$ = new Subject<string>();
  private requestedView = '';
  companyNumber = '';
  company: Record<string, any> = {};
  loading = true;
  loadingError = false;
  insights: FinancialServicesInsights = { credit: null, views: [] };
  insightsState: FinancialServicesInsightsState = 'loading';
  selectedView = '';
  selectedInsight: CompanyProfileInsight | null = null;
  selectedInsightGroup: TaxInsightGroup | null = null;
  readonly confidenceLabel = insightConfidenceLabel;
  readonly confidenceColour = insightConfidenceColour;
  readonly formatInsightDate = formatInsightDate;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private companyService: CompanyService,
    private authenticationService: AuthenticationService,
    private titleService: Title,
    private pubMethods: publicMethods,
  ) {}

  ngOnInit(): void {
    this.route.queryParamMap.pipe(takeUntil(this.destroyed$)).subscribe(params => {
      this.requestedView = params.get('view') || '';
      this.applyRequestedView();
    });
    // Magic Insights load beside the profile data so a slow answer set never holds up the page.
    this.insightRequests$.pipe(
      tap(() => {
        this.insights = { credit: null, views: [] };
        this.insightsState = 'loading';
        this.closeInsight();
      }),
      switchMap(number => number
        ? this.companyService.getFinancialServicesProfileInsights(number).pipe(
          map(data => ({ data, status: 0 })),
          catchError(error => of({ data: null, status: Number(error?.status) || -1 })))
        : of({ data: null, status: -1 })),
      takeUntil(this.destroyed$),
    ).subscribe(({ data, status }) => {
      this.insights = financialServicesInsights(data);
      this.insightsState = status === 403 ? 'unavailable' : status ? 'error' : 'ready';
      this.applyRequestedView();
    });
    this.route.paramMap.pipe(
      map(params => params.get('id') || ''),
      distinctUntilChanged(),
      tap(number => {
        this.companyNumber = number;
        this.company = {};
        this.loading = true;
        this.loadingError = false;
        this.insightRequests$.next(number);
      }),
      switchMap(number => number
        ? this.companyService.getCompanyProfileData(number).pipe(catchError(() => of(null)))
        : of(null)),
      takeUntil(this.destroyed$),
    ).subscribe(data => {
      this.company = data?.company || {};
      this.loadingError = !this.company.company_overview;
      this.loading = false;
      if (!this.loadingError) {
        this.titleService.setTitle((this.company.company_overview.name || 'Company') + ' | Zint Financial Services');
      }
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  /** Opens the view named in ?view= when it exists, otherwise the first view the backend sends (FX first). */
  private applyRequestedView(): void {
    const ids = this.insights.views.map(view => view.id);
    this.selectedView = ids.includes(this.requestedView) ? this.requestedView : ids[0] || '';
  }

  selectView(view: string): void {
    if (view === this.selectedView) return;
    this.selectedView = view;
    // As in the Tax profile: keep the view in the URL without adding history entries or scrolling to the top.
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { view },
      queryParamsHandling: 'merge',
      replaceUrl: true,
      scroll: 'manual',
    });
  }

  get activeView(): FinancialServicesView | null {
    return this.insights.views.find(view => view.id === this.selectedView) || null;
  }

  retryInsights(): void {
    this.insightRequests$.next(this.companyNumber);
  }

  sourceNote(group: TaxInsightGroup): string {
    return group.referenceCount === 1 ? '1 cited source' : group.referenceCount ? `${group.referenceCount} cited sources` : 'No sources cited';
  }

  openInsight(group: TaxInsightGroup, insight?: CompanyProfileInsight): void {
    this.selectedInsightGroup = group;
    this.selectedInsight = insight || group.cards[0] || null;
  }

  closeInsight(): void {
    this.selectedInsight = null;
    this.selectedInsightGroup = null;
  }

  get segment(): { label: string; range: string } | null {
    return bankingSegment(this.company?.company_overview?.revenue);
  }

  get productsAndServices(): string[] {
    return companyProfileOfferingItems(this.company?.company_overview);
  }

  /** Banker, auditor and accountant as recorded from the latest accounts. */
  get advisers(): Array<{ role: string; name: string }> {
    const providers = this.company?.financials?.financial_providers || {};
    return ['banker', 'auditor', 'accountant'].map(role => ({ role, name: String(providers[role] || '') }));
  }

  get logoDomain(): string {
    const overview = this.company?.company_overview;
    const domain = String(overview?.domain || '').trim();
    const url = companyProfileSafeUrl(/^https?:\/\//i.test(domain) ? domain : domain && `https://${domain}`) || companyProfileSafeUrl(overview?.url);
    return url ? new URL(url).hostname : '';
  }

  openFullProfile(): void {
    this.authenticationService.setDefaultSkin('full').subscribe({
      next: () => this.router.navigate(['/profile', this.companyNumber], {
        queryParams: this.route.snapshot.queryParams,
        fragment: this.route.snapshot.fragment,
      }),
      error: () => this.pubMethods.showInfoMessage('Could not open the full profile. Please try again.'),
    });
  }
}
